import { useMemo } from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  PollingSnapshot,
  PartyStanding,
  DetailedPollResults,
} from '@/types'
import { isDetailedPollResults } from '@/types'
import { POLL_SCOPES } from '@/constants/polling'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { VoteShareBar } from './VoteShareBar'

interface PollingDetailViewProps {
  poll: PollingSnapshot | null
  playerPartyId: string
  previousPoll?: PollingSnapshot | null
  targetName?: string
  isOpen: boolean
  onClose: () => void
}

/**
 * Modal view showing full polling details including:
 * - All party standings with error bars
 * - Cohort breakdown (detailed polls)
 * - Top issues (detailed polls)
 * - Swing cohorts (detailed polls)
 * - Trend comparison to previous poll
 */
export function PollingDetailView({
  poll,
  playerPartyId,
  previousPoll,
  targetName,
  isOpen,
  onClose,
}: PollingDetailViewProps) {
  // Sort standings by vote share
  const sortedStandings = useMemo(
    () =>
      poll
        ? [...poll.results.party_standings].sort((a, b) => b.vote_share - a.vote_share)
        : [],
    [poll]
  )

  // Calculate trends if we have a previous poll
  const trends = useMemo(() => {
    if (!poll || !previousPoll) return null

    const trendMap = new Map<
      string,
      { direction: 'up' | 'down' | 'stable'; change: number }
    >()

    for (const standing of poll.results.party_standings) {
      const prevStanding = previousPoll.results.party_standings.find(
        (s) => s.party_id === standing.party_id
      )
      if (prevStanding) {
        const change = standing.vote_share - prevStanding.vote_share
        const direction =
          change > 0.5 ? 'up' : change < -0.5 ? 'down' : 'stable'
        trendMap.set(standing.party_id, {
          direction,
          change: Math.round(change * 10) / 10,
        })
      }
    }

    return trendMap
  }, [poll, previousPoll])

  if (!poll) return null

  const scopeConfig = POLL_SCOPES[poll.poll_type]
  const isDetailed = isDetailedPollResults(poll.results)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{scopeConfig.icon}</span>
            <span>
              {scopeConfig.label} Poll - Tick {poll.tick}
            </span>
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <Badge variant={isDetailed ? 'default' : 'secondary'}>
              {isDetailed ? 'Detailed' : 'Basic'}
            </Badge>
            <span>{poll.sample_size.toLocaleString()} respondents</span>
            <span>•</span>
            <span>±{poll.margin_of_error.toFixed(1)}% margin</span>
            {targetName && poll.poll_type !== 'national' && (
              <>
                <span>•</span>
                <span>{targetName}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Party Standings */}
          <section>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Party Standings
            </h3>
            <div className="space-y-4">
              {sortedStandings.map((standing) => (
                <VoteShareBar
                  key={standing.party_id}
                  partyName={standing.party_name}
                  partyColor={standing.party_color}
                  voteShare={standing.vote_share}
                  voteLow={standing.vote_share_low}
                  voteHigh={standing.vote_share_high}
                  isPlayerParty={standing.party_id === playerPartyId}
                />
              ))}
            </div>
          </section>

          {/* Detailed poll sections */}
          {isDetailed && (
            <>
              {/* Cohort Breakdown */}
              <CohortBreakdownSection
                results={poll.results as DetailedPollResults}
              />

              {/* Top Issues */}
              <TopIssuesSection results={poll.results as DetailedPollResults} />

              {/* Swing Cohorts */}
              <SwingCohortsSection
                results={poll.results as DetailedPollResults}
              />
            </>
          )}

          {/* Trend comparison */}
          {trends && previousPoll && (
            <TrendSection
              sortedStandings={sortedStandings}
              trends={trends}
              playerPartyId={playerPartyId}
              previousTick={previousPoll.tick}
            />
          )}

          {/* Methodology note */}
          <section className="pt-4 border-t">
            <p className="text-xs text-muted-foreground italic">
              {poll.results.methodology_note}
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// --- Sub-components for detailed poll sections ---

function CohortBreakdownSection({
  results,
}: {
  results: DetailedPollResults
}) {
  if (!results.cohort_breakdown || results.cohort_breakdown.length === 0) {
    return null
  }

  return (
    <section className="pt-4 border-t">
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <span>📊</span> Cohort Breakdown
      </h3>
      <div className="space-y-2">
        {results.cohort_breakdown.map((cohort, i) => (
          <div
            key={i}
            className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0"
          >
            <span className="text-muted-foreground">{cohort.cohort_description}</span>
            <span className="font-medium">
              → {cohort.leading_party_name}{' '}
              <span
                className={cn(
                  cohort.margin >= 10
                    ? 'text-green-600'
                    : cohort.margin >= 5
                      ? 'text-yellow-600'
                      : 'text-muted-foreground'
                )}
              >
                +{cohort.margin}
              </span>
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

function TopIssuesSection({ results }: { results: DetailedPollResults }) {
  if (!results.top_issues || results.top_issues.length === 0) {
    return null
  }

  return (
    <section className="pt-4 border-t">
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <span>🔥</span> Top Issues
      </h3>
      <div className="space-y-3">
        {results.top_issues.map((issue, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">
                {i + 1}. {issue.axis_name}
              </span>
              <span className="text-xs text-muted-foreground">
                {Math.round(issue.importance * 100)}% cite as top issue
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Voters favor: {issue.favored_direction}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

function SwingCohortsSection({ results }: { results: DetailedPollResults }) {
  if (!results.swing_cohorts || results.swing_cohorts.length === 0) {
    return null
  }

  return (
    <section className="pt-4 border-t">
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <span>🔄</span> Swing Cohorts
      </h3>
      <p className="text-xs text-muted-foreground mb-2">
        These voter groups are persuadable and may shift allegiance.
      </p>
      <div className="space-y-2">
        {results.swing_cohorts.map((cohort, i) => (
          <div
            key={i}
            className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0"
          >
            <span>{cohort.cohort_description}</span>
            <Badge
              variant={cohort.volatility >= 0.7 ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {cohort.volatility >= 0.7
                ? 'High swing'
                : cohort.volatility >= 0.4
                  ? 'Med swing'
                  : 'Low swing'}
            </Badge>
          </div>
        ))}
      </div>
    </section>
  )
}

function TrendSection({
  sortedStandings,
  trends,
  playerPartyId,
  previousTick,
}: {
  sortedStandings: PartyStanding[]
  trends: Map<string, { direction: 'up' | 'down' | 'stable'; change: number }>
  playerPartyId: string
  previousTick: number
}) {
  return (
    <section className="pt-4 border-t">
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <span>📈</span> Trend (vs. Tick {previousTick})
      </h3>
      <div className="space-y-2">
        {sortedStandings.map((standing) => {
          const trend = trends.get(standing.party_id)
          if (!trend) return null

          const isPlayer = standing.party_id === playerPartyId

          return (
            <div
              key={standing.party_id}
              className={cn(
                'flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0',
                isPlayer && 'bg-primary/5 -mx-2 px-2 rounded'
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-sm"
                  style={{ backgroundColor: standing.party_color }}
                />
                <span className={cn(isPlayer && 'font-semibold')}>
                  {standing.party_name}
                  {isPlayer && ' ⭐'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="tabular-nums">
                  {standing.vote_share.toFixed(1)}%
                </span>
                <TrendIndicator
                  direction={trend.direction}
                  change={trend.change}
                  isPlayer={isPlayer}
                />
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function TrendIndicator({
  direction,
  change,
  isPlayer,
}: {
  direction: 'up' | 'down' | 'stable'
  change: number
  isPlayer: boolean
}) {
  const Icon =
    direction === 'up'
      ? TrendingUp
      : direction === 'down'
        ? TrendingDown
        : Minus

  const colorClass =
    direction === 'up'
      ? 'text-green-600'
      : direction === 'down'
        ? 'text-red-600'
        : 'text-muted-foreground'

  const prefix = change > 0 ? '+' : ''

  return (
    <div className={cn('flex items-center gap-1 text-sm', colorClass)}>
      <Icon className="h-4 w-4" />
      <span className={cn('tabular-nums', isPlayer && 'font-semibold')}>
        {prefix}
        {change.toFixed(1)}%
      </span>
      {isPlayer && direction === 'up' && (
        <span className="ml-1">📈</span>
      )}
    </div>
  )
}
